import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getGraphQLClient,
  GET_SCENE_EDITOR,
  UPDATE_SCENE,
  UPDATE_SCENE_BODY,
  GENERATE_SCENE_PLAN_POINT_SUGGESTIONS,
  SAVE_SCENE_PLAN_POINT,
  DELETE_SCENE_PLAN_POINT,
  LOCK_SCENE_PLAN_POINT,
  UNLOCK_SCENE_PLAN_POINT,
  START_SCENE_INTERVIEW,
  SEND_SCENE_INTERVIEW_MESSAGE,
  UPDATE_SCENE_INTERVIEW_MESSAGE,
  CLEAR_SCENE_INTERVIEW,
  SWITCH_SCENE_INTERVIEW_ANGLE,
  UPDATE_WRITING_STYLE,
  GENERATE_WRITING_STYLE,
  GENERATE_SCENE_PREVIEW,
  EXPAND_DRAFT_BLOCK,
} from '../lib/graphql';
import { captureException } from '../lib/sentry';
import { Scene, SceneInterviewMessage } from '../types';
import { AutoResizeTextarea, Button, Card, Input, Spinner } from '../components/common';
import { ContextItemsPanel } from '../components/refine/ContextItemsPanel';
import { ClaimSuggestionPanel, TaggedSuggestion } from '../components/writing/ClaimSuggestionPanel';

type SceneEditorResponse = { scene: Scene | null };

type SceneMutationPayload = {
  success: boolean;
  errors: string[];
  scene: Scene | null;
};

type AnyScenePayloadResponse = Record<string, SceneMutationPayload | undefined>;

type GenericSuccessResponse = Record<string, { success: boolean; errors: string[] } | undefined>;

type GenerateScenePlanPointSuggestionsResponse = {
  generateScenePlanPointSuggestions: {
    errors: string[];
    suggestions: { items: TaggedSuggestion[] } | null;
  };
};

function toWordCount(value?: string | null): number {
  if (!value) return 0;
  const trimmed = value.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
}

function firstError(errors: string[] | undefined, fallback: string): string {
  return errors && errors.length > 0 ? errors[0] : fallback;
}

function toDraftMap(items: Array<{ id: string; content: string }>): Record<string, string> {
  const draftMap: Record<string, string> = {};
  items.forEach((item) => {
    draftMap[item.id] = item.content;
  });
  return draftMap;
}

interface CollapsibleSectionProps {
  title: string;
  count?: number;
  defaultOpen?: boolean;
  action?: React.ReactNode;
  children: React.ReactNode;
}

function CollapsibleSection({
  title,
  count,
  defaultOpen = false,
  action,
  children,
}: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card padding="none" className="overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-4 sm:px-5 py-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 flex-1 min-h-[44px] text-left"
        >
          <span
            className={`inline-block transition-transform text-gray-500 ${open ? 'rotate-90' : ''}`}
            aria-hidden
          >
            ▶
          </span>
          <span className="font-semibold text-gray-900 dark:text-gray-100">{title}</span>
          {typeof count === 'number' && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
              {count}
            </span>
          )}
        </button>
        {action && open && <div className="shrink-0">{action}</div>}
      </div>
      {open && (
        <div className="px-4 sm:px-5 pb-4 pt-1 border-t border-gray-100 dark:border-gray-800 space-y-3">
          {children}
        </div>
      )}
    </Card>
  );
}

export default function SceneEditorPage() {
  const { bookId, sceneId } = useParams<{ bookId: string; sceneId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [scene, setScene] = useState<Scene | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [titleDraft, setTitleDraft] = useState('');
  const [titleEditing, setTitleEditing] = useState(false);
  const [savingTitle, setSavingTitle] = useState(false);

  const [bodyDraft, setBodyDraft] = useState('');
  const [savingBody, setSavingBody] = useState(false);

  // Key moments
  const [newPlanPoint, setNewPlanPoint] = useState('');
  const [planPointDrafts, setPlanPointDrafts] = useState<Record<string, string>>({});
  const [addingPlanPoint, setAddingPlanPoint] = useState(false);
  const [savingPlanPointId, setSavingPlanPointId] = useState<string | null>(null);
  const [deletingPlanPointId, setDeletingPlanPointId] = useState<string | null>(null);
  const [togglingPlanPointId, setTogglingPlanPointId] = useState<string | null>(null);

  // Key moment AI suggestion panel
  const [suggestionPanelOpen, setSuggestionPanelOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<TaggedSuggestion[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [moreOfTagLoading, setMoreOfTagLoading] = useState(false);
  const [moreOfTag, setMoreOfTag] = useState<string | null>(null);
  const [suggestionMode, setSuggestionMode] = useState<'fresh' | 'continue' | 'mix'>('fresh');

  // Interview
  const [startingInterview, setStartingInterview] = useState(false);
  const [newInterviewMessage, setNewInterviewMessage] = useState('');
  const [sendingInterviewMessage, setSendingInterviewMessage] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingMessageContent, setEditingMessageContent] = useState('');
  const [updatingInterviewMessage, setUpdatingInterviewMessage] = useState(false);
  const [clearingInterview, setClearingInterview] = useState(false);
  const [switchingAngleId, setSwitchingAngleId] = useState<string | null>(null);
  const [angleFilter, setAngleFilter] = useState<string | null>(null); // null = "All"
  const [savingProposalContent, setSavingProposalContent] = useState<string | null>(null);
  const [dismissedProposals, setDismissedProposals] = useState<Set<string>>(new Set());

  // Writing style
  const [writingStyleDraft, setWritingStyleDraft] = useState('');
  const [styleSuggestion, setStyleSuggestion] = useState('');
  const [savingWritingStyle, setSavingWritingStyle] = useState(false);
  const [generatingWritingStyle, setGeneratingWritingStyle] = useState(false);

  // Generation
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [generatingBody, setGeneratingBody] = useState(false);

  const applyScene = useCallback((nextScene: Scene | null) => {
    setScene(nextScene);
    setTitleDraft(nextScene?.title ?? '');
    const body = nextScene?.body ?? '';
    setBodyDraft(body);
    setWritingStyleDraft(nextScene?.storyBiblePlotBeat?.draftBlock?.writingStyle ?? '');
    setPlanPointDrafts(toDraftMap(nextScene?.planPoints ?? []));
  }, []);

  const loadScene = useCallback(async () => {
    if (!sceneId) {
      setLoading(false);
      setError(t('sceneEditor.invalidScene', 'Invalid scene ID.'));
      return;
    }

    try {
      setError(null);
      const client = getGraphQLClient();
      const data = await client.request<SceneEditorResponse>(GET_SCENE_EDITOR, { sceneId });
      applyScene(data.scene);
      if (!data.scene) {
        setError(t('sceneEditor.notFound', 'Scene not found.'));
      }
    } catch (err) {
      captureException(err);
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [sceneId, t, applyScene]);

  useEffect(() => {
    void loadScene();
  }, [loadScene]);

  const activeInterview = useMemo(() => {
    const interviews = scene?.sceneInterviews ?? [];
    if (interviews.length === 0) return null;
    return interviews.find((iv) => iv.status === 'active') ?? interviews[0];
  }, [scene]);

  const angleProgress = useMemo(() => {
    const map: Record<
      string,
      { user: number; assistant: number; status: 'untouched' | 'probing' | 'probed' | 'covered' }
    > = {};
    (scene?.planPoints ?? []).forEach((ap) => {
      map[ap.id] = { user: 0, assistant: 0, status: 'untouched' };
    });
    (activeInterview?.messages ?? []).forEach((m) => {
      if (!m.scenePlanPointId || !map[m.scenePlanPointId]) return;
      if (m.role === 'user') map[m.scenePlanPointId].user += 1;
      if (m.role === 'assistant') map[m.scenePlanPointId].assistant += 1;
    });
    const completed = activeInterview?.status === 'completed';
    Object.values(map).forEach((entry) => {
      if (entry.user >= 3 || (completed && entry.user >= 1)) entry.status = 'covered';
      else if (entry.user >= 1) entry.status = 'probed';
      else if (entry.assistant >= 1) entry.status = 'probing';
      else entry.status = 'untouched';
    });
    return map;
  }, [scene, activeInterview]);

  const currentAngleId = useMemo(() => {
    const msgs = activeInterview?.messages ?? [];
    for (let i = msgs.length - 1; i >= 0; i -= 1) {
      if (msgs[i].scenePlanPointId) return msgs[i].scenePlanPointId ?? null;
    }
    return null;
  }, [activeInterview]);

  const filteredMessages = useMemo(() => {
    const msgs = activeInterview?.messages ?? [];
    if (!angleFilter) return msgs;
    return msgs.filter((m) => m.scenePlanPointId === angleFilter);
  }, [activeInterview, angleFilter]);

  const draftBlockId = scene?.storyBiblePlotBeat?.draftBlock?.id ?? null;

  const interviewRoleLabel = useCallback(
    (role: SceneInterviewMessage['role']) => {
      if (role === 'user') return t('sceneEditor.role.user', 'You');
      if (role === 'assistant') return t('sceneEditor.role.assistant', 'AI interviewer');
      if (role === 'system') return t('sceneEditor.role.system', 'System');
      return role;
    },
    [t]
  );

  const materialHealth = useMemo(() => {
    const bodyWords = toWordCount(bodyDraft);
    const scenePlan = scene?.planPoints?.length ?? 0;
    const contextItems = scene?.contextItems?.length ?? 0;
    const userMessages = (scene?.sceneInterviews ?? [])
      .flatMap((iv) => iv.messages)
      .filter((m) => m.role === 'user').length;

    let score = 0;
    if (bodyWords >= 40) score += 1;
    if (scenePlan > 0) score += 1;
    if (contextItems > 1) score += 1;
    if (userMessages > 1) score += 1;

    return {
      score,
      bodyWords,
      scenePlan,
      contextItems,
      userMessages,
      sparse: score < 3,
    };
  }, [scene, bodyDraft]);

  const sceneWordCount = toWordCount(scene?.body);

  // ---------- Generic helpers ----------

  const callMutation = useCallback(
    async <T extends AnyScenePayloadResponse | GenericSuccessResponse>(
      mutation: string,
      variables: Record<string, unknown>
    ): Promise<T | null> => {
      const client = getGraphQLClient();
      try {
        setError(null);
        setNotice(null);
        return await client.request<T>(mutation, variables);
      } catch (err) {
        captureException(err);
        setError(err instanceof Error ? err.message : t('common.error'));
        return null;
      }
    },
    [t]
  );

  const handleScenePayload = useCallback(
    (
      payload: SceneMutationPayload | undefined,
      fallbackError: string,
      successNotice?: string
    ): boolean => {
      if (!payload?.success || !payload.scene) {
        setError(firstError(payload?.errors, fallbackError));
        return false;
      }
      applyScene(payload.scene);
      if (successNotice) setNotice(successNotice);
      return true;
    },
    [applyScene]
  );

  // ---------- Title ----------

  const handleSaveTitle = async () => {
    if (!scene) return;
    const trimmed = titleDraft.trim() || scene.title;
    if (trimmed === scene.title) {
      setTitleEditing(false);
      return;
    }
    setSavingTitle(true);
    const response = await callMutation<AnyScenePayloadResponse>(UPDATE_SCENE, {
      sceneId: scene.id,
      title: trimmed,
    });
    setSavingTitle(false);
    if (response) {
      const ok = handleScenePayload(
        response.updateScene,
        t('sceneEditor.titleSaveFailed', 'Failed to save scene title.'),
        t('sceneEditor.titleSaved', 'Scene title saved.')
      );
      if (ok) setTitleEditing(false);
    }
  };

  // ---------- Body (canonical scene prose) ----------

  const currentBody = scene?.body ?? '';
  const bodyChanged = bodyDraft !== currentBody;

  const handleSaveBody = async () => {
    if (!scene) return;
    setSavingBody(true);
    const response = await callMutation<AnyScenePayloadResponse>(UPDATE_SCENE_BODY, {
      sceneId: scene.id,
      content: bodyDraft,
    });
    setSavingBody(false);
    if (response) {
      handleScenePayload(
        response.updateSceneBody,
        t('sceneEditor.bodySaveFailed', 'Failed to save scene body.'),
        t('sceneEditor.bodySaved', 'Scene body saved.')
      );
    }
  };

  // ---------- Key moments ----------

  const handleAddPlanPoint = async (contentOverride?: string) => {
    if (!scene) return;
    const content = (contentOverride ?? newPlanPoint).trim();
    if (!content) return;
    setAddingPlanPoint(true);
    const response = await callMutation<GenericSuccessResponse>(SAVE_SCENE_PLAN_POINT, {
      sceneId: scene.id,
      content,
      position: scene.planPoints?.length ?? 0,
    });
    setAddingPlanPoint(false);
    if (response?.saveScenePlanPoint) {
      if (response.saveScenePlanPoint.errors.length > 0) {
        setError(
          firstError(
            response.saveScenePlanPoint.errors,
            t('sceneEditor.planPointSaveFailed', 'Failed to save key moment.')
          )
        );
        return;
      }
      if (!contentOverride) setNewPlanPoint('');
      await loadScene();
      setNotice(t('sceneEditor.planPointAdded', 'Key moment added.'));
    }
  };

  const handleTogglePlanPointLock = async (id: string, locked: boolean) => {
    setTogglingPlanPointId(id);
    const response = await callMutation<GenericSuccessResponse>(
      locked ? UNLOCK_SCENE_PLAN_POINT : LOCK_SCENE_PLAN_POINT,
      { planPointId: id }
    );
    setTogglingPlanPointId(null);
    if (response) {
      const payload = locked ? response.unlockScenePlanPoint : response.lockScenePlanPoint;
      if (!payload?.success) {
        setError(
          firstError(
            payload?.errors,
            t('sceneEditor.planPointLockFailed', 'Failed to update key moment lock.')
          )
        );
        return;
      }
      await loadScene();
    }
  };

  // ---------- Key moment AI suggestions ----------

  const fetchSuggestions = useCallback(
    async (opts: { focusTag?: string; mode?: 'fresh' | 'continue' | 'mix' } = {}) => {
      if (!scene) return;
      const { focusTag, mode } = opts;
      try {
        const client = getGraphQLClient();
        const response = await client.request<GenerateScenePlanPointSuggestionsResponse>(
          GENERATE_SCENE_PLAN_POINT_SUGGESTIONS,
          {
            sceneId: scene.id,
            focusTag: focusTag ?? undefined,
            mode: mode ?? suggestionMode,
          }
        );
        const items = response.generateScenePlanPointSuggestions?.suggestions?.items ?? [];
        if (focusTag) {
          setSuggestions((prev) => [...prev, ...items]);
        } else {
          setSuggestions(items);
        }
      } catch (err) {
        captureException(err);
        setError(err instanceof Error ? err.message : t('common.error'));
      }
    },
    [scene, t, suggestionMode]
  );

  const handleOpenSuggestions = async () => {
    // Auto-pick the default mode based on body length: short bodies get
    // fresh ideas (the old claim-style brainstorm), substantial bodies get
    // continuation suggestions that ride the existing prose.
    const FRESH_THRESHOLD_WORDS = 200;
    const defaultMode: 'fresh' | 'continue' =
      sceneWordCount < FRESH_THRESHOLD_WORDS ? 'fresh' : 'continue';
    setSuggestionMode(defaultMode);
    setSuggestionPanelOpen(true);
    setSuggestionsLoading(true);
    setSuggestions([]);
    await fetchSuggestions({ mode: defaultMode });
    setSuggestionsLoading(false);
  };

  const handleRefreshSuggestions = async () => {
    setSuggestionsLoading(true);
    setSuggestions([]);
    await fetchSuggestions({ mode: suggestionMode });
    setSuggestionsLoading(false);
  };

  const handleChangeSuggestionMode = async (mode: 'fresh' | 'continue' | 'mix') => {
    if (mode === suggestionMode) return;
    setSuggestionMode(mode);
    setSuggestionsLoading(true);
    setSuggestions([]);
    await fetchSuggestions({ mode });
    setSuggestionsLoading(false);
  };

  const handleMoreOfTag = async (tag: string) => {
    setMoreOfTagLoading(true);
    setMoreOfTag(tag);
    await fetchSuggestions({ focusTag: tag, mode: suggestionMode });
    setMoreOfTagLoading(false);
    setMoreOfTag(null);
  };

  const handleAddSuggested = async (content: string) => {
    await handleAddPlanPoint(content);
  };

  const handleSavePlanPoint = async (id: string) => {
    if (!scene) return;
    const content = planPointDrafts[id]?.trim();
    if (!content) {
      setError(t('sceneEditor.planPointEmpty', 'Key moment cannot be empty.'));
      return;
    }
    setSavingPlanPointId(id);
    const response = await callMutation<GenericSuccessResponse>(SAVE_SCENE_PLAN_POINT, {
      sceneId: scene.id,
      planPointId: id,
      content,
    });
    setSavingPlanPointId(null);
    if (response?.saveScenePlanPoint) {
      if (response.saveScenePlanPoint.errors.length > 0) {
        setError(
          firstError(
            response.saveScenePlanPoint.errors,
            t('sceneEditor.planPointSaveFailed', 'Failed to save key moment.')
          )
        );
        return;
      }
      await loadScene();
      setNotice(t('sceneEditor.planPointSaved', 'Key moment saved.'));
    }
  };

  const handleDeletePlanPoint = async (id: string) => {
    setDeletingPlanPointId(id);
    const response = await callMutation<GenericSuccessResponse>(DELETE_SCENE_PLAN_POINT, {
      planPointId: id,
    });
    setDeletingPlanPointId(null);
    if (response?.deleteScenePlanPoint) {
      if (!response.deleteScenePlanPoint.success) {
        setError(
          firstError(
            response.deleteScenePlanPoint.errors,
            t('sceneEditor.planPointDeleteFailed', 'Failed to delete key moment.')
          )
        );
        return;
      }
      await loadScene();
      setNotice(t('sceneEditor.planPointDeleted', 'Key moment deleted.'));
    }
  };

  // ---------- Interview ----------

  const handleStartInterview = async () => {
    if (!scene) return;
    setStartingInterview(true);
    const response = await callMutation<GenericSuccessResponse>(START_SCENE_INTERVIEW, {
      sceneId: scene.id,
    });
    setStartingInterview(false);
    if (response?.startSceneInterview) {
      if (!response.startSceneInterview.success) {
        setError(
          firstError(
            response.startSceneInterview.errors,
            t('sceneEditor.interviewStartFailed', 'Failed to start interview.')
          )
        );
        return;
      }
      await loadScene();
      setNotice(t('sceneEditor.interviewStarted', 'Interview started.'));
    }
  };

  const handleAcceptProposedAngle = async (content: string) => {
    if (!scene) return;
    setSavingProposalContent(content);
    const response = await callMutation<GenericSuccessResponse>(SAVE_SCENE_PLAN_POINT, {
      sceneId: scene.id,
      content,
      position: scene.planPoints?.length ?? 0,
    });
    setSavingProposalContent(null);
    if (response?.saveScenePlanPoint) {
      if (response.saveScenePlanPoint.errors.length > 0) {
        setError(
          firstError(
            response.saveScenePlanPoint.errors,
            t('sceneEditor.planPointSaveFailed', 'Failed to save key moment.')
          )
        );
        return;
      }
      setDismissedProposals((prev) => new Set(prev).add(content));
      await loadScene();
    }
  };

  const handleDismissProposedAngle = (content: string) => {
    setDismissedProposals((prev) => new Set(prev).add(content));
  };

  const handleSwitchAngle = async (planPointId: string) => {
    if (!activeInterview) {
      await handleStartInterview();
      return;
    }
    setSwitchingAngleId(planPointId);
    const response = await callMutation<GenericSuccessResponse>(SWITCH_SCENE_INTERVIEW_ANGLE, {
      interviewId: activeInterview.id,
      scenePlanPointId: planPointId,
    });
    setSwitchingAngleId(null);
    if (response?.switchSceneInterviewAngle) {
      if (!response.switchSceneInterviewAngle.success) {
        setError(
          firstError(
            response.switchSceneInterviewAngle.errors,
            t('sceneEditor.interviewSwitchFailed', 'Failed to switch angle.')
          )
        );
        return;
      }
      setAngleFilter(planPointId);
      await loadScene();
    }
  };

  const handleSendInterviewMessage = async () => {
    if (!activeInterview || !newInterviewMessage.trim()) return;
    setSendingInterviewMessage(true);
    const response = await callMutation<GenericSuccessResponse>(SEND_SCENE_INTERVIEW_MESSAGE, {
      interviewId: activeInterview.id,
      content: newInterviewMessage.trim(),
    });
    setSendingInterviewMessage(false);
    if (response?.sendSceneInterviewMessage) {
      if (!response.sendSceneInterviewMessage.success) {
        setError(
          firstError(
            response.sendSceneInterviewMessage.errors,
            t('sceneEditor.interviewSendFailed', 'Failed to send message.')
          )
        );
        return;
      }
      setNewInterviewMessage('');
      await loadScene();
    }
  };

  const handleSaveInterviewMessage = async () => {
    if (!editingMessageId || !editingMessageContent.trim()) return;
    setUpdatingInterviewMessage(true);
    const response = await callMutation<GenericSuccessResponse>(UPDATE_SCENE_INTERVIEW_MESSAGE, {
      messageId: editingMessageId,
      content: editingMessageContent.trim(),
    });
    setUpdatingInterviewMessage(false);
    if (response?.updateSceneInterviewMessage) {
      if (!response.updateSceneInterviewMessage.success) {
        setError(
          firstError(
            response.updateSceneInterviewMessage.errors,
            t('sceneEditor.interviewUpdateFailed', 'Failed to update message.')
          )
        );
        return;
      }
      setEditingMessageId(null);
      setEditingMessageContent('');
      await loadScene();
    }
  };

  const handleClearInterview = async () => {
    if (!activeInterview) return;
    setClearingInterview(true);
    const response = await callMutation<GenericSuccessResponse>(CLEAR_SCENE_INTERVIEW, {
      interviewId: activeInterview.id,
    });
    setClearingInterview(false);
    if (response?.clearSceneInterview) {
      if (!response.clearSceneInterview.success) {
        setError(
          firstError(
            response.clearSceneInterview.errors,
            t('sceneEditor.interviewClearFailed', 'Failed to clear interview.')
          )
        );
        return;
      }
      await loadScene();
      setNotice(t('sceneEditor.interviewCleared', 'Interview cleared.'));
    }
  };

  // ---------- Writing style ----------

  const handleSaveWritingStyle = async () => {
    if (!draftBlockId || !writingStyleDraft.trim()) return;
    setSavingWritingStyle(true);
    const response = await callMutation<GenericSuccessResponse>(UPDATE_WRITING_STYLE, {
      draftBlockId,
      writingStyle: writingStyleDraft.trim(),
      regeneratePreview: true,
    });
    setSavingWritingStyle(false);
    if (response?.updateWritingStyle) {
      if (!response.updateWritingStyle.success) {
        setError(
          firstError(
            response.updateWritingStyle.errors,
            t('sceneEditor.styleSaveFailed', 'Failed to save writing style.')
          )
        );
        return;
      }
      await loadScene();
      setNotice(t('sceneEditor.styleSaved', 'Writing style saved.'));
    }
  };

  const handleGenerateWritingStyle = async () => {
    if (!draftBlockId) return;
    setGeneratingWritingStyle(true);
    const response = await callMutation<GenericSuccessResponse>(GENERATE_WRITING_STYLE, {
      draftBlockId,
      suggestion: styleSuggestion.trim() || undefined,
      autoRegeneratePreview: true,
    });
    setGeneratingWritingStyle(false);
    if (response?.generateWritingStyle) {
      if (!response.generateWritingStyle.success) {
        setError(
          firstError(
            response.generateWritingStyle.errors,
            t('sceneEditor.styleGenerateFailed', 'Failed to generate writing style.')
          )
        );
        return;
      }
      setStyleSuggestion('');
      await loadScene();
      setNotice(t('sceneEditor.styleGenerated', 'Writing style generation started.'));
    }
  };

  // ---------- Generation ----------

  const handleGeneratePreview = async () => {
    if (!scene) return;
    setGeneratingPreview(true);
    const response = await callMutation<GenericSuccessResponse>(GENERATE_SCENE_PREVIEW, {
      sceneId: scene.id,
    });
    setGeneratingPreview(false);
    if (response?.generateScenePreview) {
      if (!response.generateScenePreview.success) {
        setError(
          firstError(
            response.generateScenePreview.errors,
            t('sceneEditor.previewGenerateFailed', 'Failed to generate preview.')
          )
        );
        return;
      }
      await loadScene();
      setNotice(t('sceneEditor.previewGenerated', 'Preview generation started.'));
    }
  };

  const handleGenerateBody = async () => {
    if (!bookId || !draftBlockId || !scene) return;
    setGeneratingBody(true);
    try {
      // Ensure preview content exists first
      if (!scene.previewContent) {
        const previewResponse = await callMutation<GenericSuccessResponse>(GENERATE_SCENE_PREVIEW, {
          sceneId: scene.id,
          force: true,
        });
        if (!previewResponse?.generateScenePreview?.success) {
          setError(
            firstError(
              previewResponse?.generateScenePreview?.errors,
              t('sceneEditor.previewGenerateFailed', 'Failed to generate preview.')
            )
          );
          return;
        }
        await loadScene();
      }
      const response = await callMutation<GenericSuccessResponse>(EXPAND_DRAFT_BLOCK, {
        bookId,
        draftBlockId,
        force: true,
      });
      if (!response?.expandDraftBlock?.success) {
        setError(
          firstError(
            response?.expandDraftBlock?.errors,
            t('sceneEditor.bodyGenerateFailed', 'Failed to generate full scene text.')
          )
        );
        return;
      }
      setNotice(
        t(
          'sceneEditor.bodyGenerationStarted',
          'Full scene body generation started. It may take a few minutes.'
        )
      );
    } finally {
      setGeneratingBody(false);
    }
  };

  // ---------- Render ----------

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Spinner />
      </div>
    );
  }

  if (!scene || !bookId) {
    return (
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <Card>
          <div className="space-y-4">
            <p className="text-red-600">{error || t('sceneEditor.notFound', 'Scene not found.')}</p>
            <Button onClick={() => navigate(`/books/${bookId ?? ''}/refine`)}>
              {t('chaptersFirst.backToWorkspace', 'Back to workspace')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const bodyEmpty = !bodyDraft.trim() && !currentBody.trim();

  return (
    <div className="min-h-screen pb-32 sm:pb-24 bg-gray-50 dark:bg-gray-950">
      {/* Sticky compact header */}
      <header className="sticky top-0 z-20 bg-white/95 dark:bg-gray-900/95 backdrop-blur border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 space-y-2">
          <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400 overflow-x-auto">
            <button
              onClick={() => navigate(`/books/${bookId}/refine`)}
              className="hover:underline whitespace-nowrap shrink-0"
            >
              {t('chaptersFirst.backToWorkspace', 'Refine')}
            </button>
            <span>›</span>
            <span className="truncate">{t('sceneEditor.sceneOverview', 'Scene')}</span>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {titleEditing ? (
              <div className="flex-1 flex items-center gap-2">
                <Input
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void handleSaveTitle();
                    if (e.key === 'Escape') {
                      setTitleDraft(scene.title);
                      setTitleEditing(false);
                    }
                  }}
                  className="flex-1"
                />
                <Button size="sm" isLoading={savingTitle} onClick={handleSaveTitle}>
                  {t('common.save', 'Save')}
                </Button>
              </div>
            ) : (
              <button
                onClick={() => setTitleEditing(true)}
                className="flex-1 text-left text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 rounded px-2 -mx-2 py-1 min-h-[44px]"
                title={t('sceneEditor.tapToEdit', 'Tap to edit title')}
              >
                {titleDraft || scene.title}
              </button>
            )}
            <div className="flex flex-wrap gap-1.5 text-[11px]">
              {scene.locked && (
                <span className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800">
                  🔒 {t('chaptersFirst.locked', 'Locked')}
                </span>
              )}
              {scene.previewStale && (
                <span className="px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200">
                  ⚠ {t('chaptersFirst.stale', 'Stale')}
                </span>
              )}
              <a
                href="#scene-body"
                onClick={(e) => {
                  e.preventDefault();
                  document
                    .getElementById('scene-body')
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className="px-2 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-200 hover:bg-indigo-100 dark:hover:bg-indigo-900/50"
                title={t('sceneEditor.jumpToBody', 'Jump to scene body')}
              >
                ↓ {sceneWordCount} {t('chaptersFirst.words', 'words')}
              </a>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 space-y-4">
        {error && (
          <div className="rounded-lg p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}
        {notice && (
          <div className="rounded-lg p-3 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 text-sm">
            {notice}
          </div>
        )}

        <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)] lg:gap-6 space-y-4 lg:space-y-0">
          {/* LEFT COLUMN — inputs feed the body */}
          <div className="space-y-4">
            {/* KEY MOMENTS — the unified beats list */}
            <CollapsibleSection
              title={t('sceneEditor.scenePlanTitle', 'Key moments')}
              count={scene.planPoints?.length ?? 0}
              defaultOpen={(scene.planPoints?.length ?? 0) > 0}
              action={
                <Button
                  size="sm"
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleOpenSuggestions();
                  }}
                >
                  ✨ {t('sceneEditor.scenePlanSuggest', 'Suggest')}
                </Button>
              }
            >
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t(
                  'sceneEditor.scenePlanHint',
                  'Beats this scene must hit — angles, moments, callbacks, sensory anchors. Lock the ones you want preserved through regeneration.'
                )}
              </p>
              <div className="space-y-3">
                {(scene.planPoints ?? []).map((ap) => (
                  <div
                    key={ap.id}
                    className="border border-gray-200 dark:border-gray-700 rounded-md p-3 space-y-2"
                  >
                    <AutoResizeTextarea
                      minRows={2}
                      value={planPointDrafts[ap.id] ?? ''}
                      onChange={(e) =>
                        setPlanPointDrafts((prev) => ({ ...prev, [ap.id]: e.target.value }))
                      }
                    />
                    <div className="flex flex-wrap justify-between gap-2 items-center">
                      <span className="text-xs text-gray-500">
                        {ap.locked
                          ? t('sceneEditor.planPointLockedStatus', 'Locked')
                          : t('sceneEditor.planPointUnlockedStatus', 'Unlocked')}
                      </span>
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleTogglePlanPointLock(ap.id, ap.locked)}
                          isLoading={togglingPlanPointId === ap.id}
                        >
                          {ap.locked
                            ? t('sceneEditor.unlockPlanPoint', 'Unlock')
                            : t('sceneEditor.lockPlanPoint', 'Lock')}
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={() => handleDeletePlanPoint(ap.id)}
                          isLoading={deletingPlanPointId === ap.id}
                        >
                          {t('common.delete', 'Delete')}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleSavePlanPoint(ap.id)}
                          isLoading={savingPlanPointId === ap.id}
                        >
                          {t('common.save', 'Save')}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="border border-dashed border-gray-300 dark:border-gray-700 rounded-md p-3 space-y-2">
                <AutoResizeTextarea
                  minRows={2}
                  value={newPlanPoint}
                  onChange={(e) => setNewPlanPoint(e.target.value)}
                  placeholder={t(
                    'sceneEditor.planPointPlaceholder',
                    'Example: Reveal the secret in the second-to-last beat.'
                  )}
                />
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    onClick={() => handleAddPlanPoint()}
                    isLoading={addingPlanPoint}
                  >
                    {t('sceneEditor.addPlanPoint', 'Add key moment')}
                  </Button>
                </div>
              </div>
            </CollapsibleSection>

            {/* INTERVIEW */}
            <CollapsibleSection
              title={t('sceneEditor.interviewTitle', 'Interview')}
              count={
                (scene.sceneInterviews ?? [])
                  .flatMap((iv) => iv.messages)
                  .filter((m) => m.role === 'user').length || undefined
              }
              action={
                activeInterview ? (
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleClearInterview();
                    }}
                    isLoading={clearingInterview}
                  >
                    {t('sceneEditor.clearInterview', 'Clear')}
                  </Button>
                ) : undefined
              }
            >
              {(() => {
                const scenePlan = scene.planPoints ?? [];
                if (!activeInterview) {
                  return (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        {scenePlan.length === 0
                          ? t(
                              'sceneEditor.interviewIntroDiscovery',
                              'No key moments yet — start talking and the AI will surface angles worth tracking. You can save any of them as key moments along the way.'
                            )
                          : t(
                              'sceneEditor.interviewIntro',
                              'The AI will walk through each key moment as an angle. Click Start, or pick the angle you want to begin with.'
                            )}
                      </p>
                      {scenePlan.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {scenePlan.map((ap, idx) => (
                            <button
                              type="button"
                              key={ap.id}
                              onClick={() => handleSwitchAngle(ap.id)}
                              disabled={!!switchingAngleId || startingInterview}
                              className="rounded-full border border-gray-300 dark:border-gray-700 px-3 py-1 text-xs hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                              title={ap.content}
                            >
                              ○ A{idx + 1}: {ap.content.slice(0, 36)}
                              {ap.content.length > 36 ? '…' : ''}
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="flex justify-end">
                        <Button onClick={handleStartInterview} isLoading={startingInterview}>
                          {t('sceneEditor.startInterview', 'Start interview')}
                        </Button>
                      </div>
                    </div>
                  );
                }
                const isCompleted = activeInterview.status === 'completed';
                return (
                  <div className="space-y-3">
                    {/* Angle chips — only when angles exist */}
                    {scenePlan.length > 0 && (
                      <div className="flex flex-wrap gap-2 items-center">
                        <button
                          type="button"
                          onClick={() => setAngleFilter(null)}
                          className={`rounded-full px-3 py-1 text-xs border ${
                            angleFilter === null
                              ? 'bg-gray-900 text-white border-gray-900 dark:bg-gray-100 dark:text-gray-900 dark:border-gray-100'
                              : 'border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                          }`}
                        >
                          {t('sceneEditor.allAngles', 'All')}
                        </button>
                        {scenePlan.map((ap, idx) => {
                          const prog = angleProgress[ap.id];
                          const dot =
                            prog?.status === 'covered'
                              ? '✓'
                              : prog?.status === 'probed'
                                ? '●'
                                : prog?.status === 'probing'
                                  ? '◐'
                                  : '○';
                          const isCurrent = currentAngleId === ap.id;
                          const isFiltered = angleFilter === ap.id;
                          const baseClasses =
                            'rounded-full px-3 py-1 text-xs border transition-colors disabled:opacity-50';
                          const stateClasses = isFiltered
                            ? 'bg-gray-900 text-white border-gray-900 dark:bg-gray-100 dark:text-gray-900 dark:border-gray-100'
                            : isCurrent
                              ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-400 dark:border-blue-700'
                              : 'border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800';
                          return (
                            <button
                              type="button"
                              key={ap.id}
                              onClick={() => {
                                if (isFiltered) {
                                  setAngleFilter(null);
                                } else if (isCurrent) {
                                  setAngleFilter(ap.id);
                                } else {
                                  void handleSwitchAngle(ap.id);
                                }
                              }}
                              disabled={switchingAngleId === ap.id || isCompleted}
                              className={`${baseClasses} ${stateClasses}`}
                              title={ap.content}
                            >
                              {dot} A{idx + 1}: {ap.content.slice(0, 30)}
                              {ap.content.length > 30 ? '…' : ''}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* Chat */}
                    <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                      {filteredMessages.length === 0 ? (
                        <p className="text-xs text-gray-500 italic px-1">
                          {t(
                            'sceneEditor.noMessagesForAngle',
                            'Nothing on this angle yet — switch to it from a chip above to start.'
                          )}
                        </p>
                      ) : (
                        filteredMessages.map((message) => {
                          const angleIdx = message.scenePlanPointId
                            ? scenePlan.findIndex((ap) => ap.id === message.scenePlanPointId)
                            : -1;
                          const angleLabel = angleIdx >= 0 ? `A${angleIdx + 1}` : null;
                          return (
                            <div
                              key={message.id}
                              className={`rounded-md p-3 border text-sm ${
                                message.role === 'assistant'
                                  ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-100 dark:border-blue-900'
                                  : message.role === 'system'
                                    ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-100 dark:border-purple-900'
                                    : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                              }`}
                            >
                              <div className="flex justify-between items-center mb-1">
                                <span className="text-xs uppercase tracking-wide text-gray-500 flex items-center gap-2">
                                  {interviewRoleLabel(message.role)}
                                  {angleLabel && (
                                    <span className="rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 px-1.5 py-0.5 text-[10px] font-mono">
                                      {angleLabel}
                                    </span>
                                  )}
                                </span>
                                {message.role === 'user' && editingMessageId !== message.id && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                      setEditingMessageId(message.id);
                                      setEditingMessageContent(message.content);
                                    }}
                                  >
                                    {t('common.edit', 'Edit')}
                                  </Button>
                                )}
                              </div>
                              {editingMessageId === message.id ? (
                                <div className="space-y-2">
                                  <AutoResizeTextarea
                                    minRows={2}
                                    value={editingMessageContent}
                                    onChange={(e) => setEditingMessageContent(e.target.value)}
                                  />
                                  <div className="flex justify-end gap-2">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => {
                                        setEditingMessageId(null);
                                        setEditingMessageContent('');
                                      }}
                                    >
                                      {t('common.cancel', 'Cancel')}
                                    </Button>
                                    <Button
                                      size="sm"
                                      onClick={handleSaveInterviewMessage}
                                      isLoading={updatingInterviewMessage}
                                    >
                                      {t('common.save', 'Save')}
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <p className="whitespace-pre-wrap">{message.content}</p>
                              )}

                              {message.role === 'assistant' &&
                                (message.proposedAngles ?? []).length > 0 &&
                                (() => {
                                  const savedSet = new Set(
                                    scenePlan.map((ap) => ap.content.trim().toLowerCase())
                                  );
                                  const pending = (message.proposedAngles ?? []).filter(
                                    (p) =>
                                      !savedSet.has(p.trim().toLowerCase()) &&
                                      !dismissedProposals.has(p)
                                  );
                                  if (pending.length === 0) return null;
                                  return (
                                    <div className="mt-3 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-2 space-y-2">
                                      <p className="text-xs text-amber-900 dark:text-amber-200 font-medium">
                                        {t(
                                          'sceneEditor.proposedAnglesHint',
                                          'Worth saving as key moments?'
                                        )}
                                      </p>
                                      <ul className="space-y-1">
                                        {pending.map((proposal) => (
                                          <li
                                            key={proposal}
                                            className="flex items-start gap-2 text-sm"
                                          >
                                            <span className="flex-1 text-gray-800 dark:text-gray-200">
                                              {proposal}
                                            </span>
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              onClick={() => handleDismissProposedAngle(proposal)}
                                              disabled={savingProposalContent === proposal}
                                            >
                                              {t('common.dismiss', 'Dismiss')}
                                            </Button>
                                            <Button
                                              size="sm"
                                              onClick={() => handleAcceptProposedAngle(proposal)}
                                              isLoading={savingProposalContent === proposal}
                                            >
                                              {t('sceneEditor.saveAsPlanPoint', 'Save')}
                                            </Button>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  );
                                })()}
                            </div>
                          );
                        })
                      )}
                    </div>

                    {isCompleted ? (
                      <div className="rounded-md border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-3 text-sm text-green-800 dark:text-green-200">
                        {t(
                          'sceneEditor.interviewComplete',
                          'Interview complete — all angles covered. Ready to use this material to generate prose.'
                        )}
                      </div>
                    ) : (
                      <>
                        <AutoResizeTextarea
                          minRows={2}
                          value={newInterviewMessage}
                          onChange={(e) => setNewInterviewMessage(e.target.value)}
                          placeholder={t(
                            'sceneEditor.interviewReplyPlaceholder',
                            'Answer with concrete details, sensory cues, specific intent.'
                          )}
                        />
                        <div className="flex justify-end">
                          <Button
                            onClick={handleSendInterviewMessage}
                            isLoading={sendingInterviewMessage}
                            disabled={!newInterviewMessage.trim()}
                          >
                            {t('sceneEditor.sendReply', 'Send reply')}
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}
            </CollapsibleSection>

            {/* WRITING STYLE */}
            <CollapsibleSection title={t('sceneEditor.styleTitle', 'Writing style')}>
              {!draftBlockId ? (
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  {t(
                    'sceneEditor.styleUnavailable',
                    'This scene is not yet linked to a draft block, so style override is unavailable.'
                  )}
                </p>
              ) : (
                <>
                  <AutoResizeTextarea
                    minRows={3}
                    value={writingStyleDraft}
                    onChange={(e) => setWritingStyleDraft(e.target.value)}
                    placeholder={t(
                      'sceneEditor.stylePlaceholder',
                      'Voice, rhythm, tone for this scene.'
                    )}
                  />
                  <Input
                    label={t('sceneEditor.styleSuggestionLabel', 'AI style guidance (optional)')}
                    value={styleSuggestion}
                    onChange={(e) => setStyleSuggestion(e.target.value)}
                  />
                  <div className="flex flex-wrap gap-2 justify-end">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleGenerateWritingStyle}
                      isLoading={generatingWritingStyle}
                      disabled={savingWritingStyle}
                    >
                      {t('sceneEditor.generateStyle', 'Generate with AI')}
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSaveWritingStyle}
                      isLoading={savingWritingStyle}
                      disabled={generatingWritingStyle || !writingStyleDraft.trim()}
                    >
                      {t('sceneEditor.saveStyle', 'Save style')}
                    </Button>
                  </div>
                  {(scene.storyBiblePlotBeat?.draftBlock?.writingStyleSuggestions ?? []).length >
                    0 && (
                    <div className="flex flex-wrap gap-2">
                      {(scene.storyBiblePlotBeat?.draftBlock?.writingStyleSuggestions ?? []).map(
                        (suggestion, i) => (
                          <button
                            key={`${suggestion}-${i}`}
                            type="button"
                            onClick={() => setWritingStyleDraft(suggestion)}
                            className="text-sm px-3 py-1.5 rounded-full border border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30"
                          >
                            {suggestion}
                          </button>
                        )
                      )}
                    </div>
                  )}
                </>
              )}
            </CollapsibleSection>

            {/* CONTEXT MATERIAL */}
            <CollapsibleSection
              title={t('sceneEditor.contextTitle', 'Context material')}
              count={scene.contextItems?.length ?? 0}
            >
              <ContextItemsPanel
                contextableType="Scene"
                contextableId={scene.id}
                items={scene.contextItems ?? []}
                onChanged={loadScene}
                onError={setError}
                title=""
                emptyHint={t(
                  'sceneEditor.contextHint',
                  'Facts, references, notes, source details — improve generation quality.'
                )}
              />
            </CollapsibleSection>

            {materialHealth.sparse && !bodyEmpty && (
              <div className="p-3 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-200 text-sm">
                {t(
                  'sceneEditor.sparseWarning',
                  'Scene material is still sparse. AI regeneration may produce lower-quality output.'
                )}
              </div>
            )}
          </div>

          {/* RIGHT COLUMN — the canonical body */}
          <div className="space-y-4" id="scene-body">
            <Card padding="lg" className="space-y-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    {t('sceneEditor.bodyTitle', 'Scene body')}
                  </h2>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {t('sceneEditor.bodyHint', 'The prose your reader will see')}
                  </span>
                </div>
                <Button
                  size="sm"
                  onClick={handleSaveBody}
                  isLoading={savingBody}
                  disabled={!bodyChanged}
                >
                  {t('common.save', 'Save')}
                </Button>
              </div>

              {/* Generate actions — the seam between inputs and prose */}
              <div className="flex flex-wrap gap-2 items-center pb-2 border-b border-gray-100 dark:border-gray-800">
                {bodyEmpty ? (
                  <>
                    <Button
                      size="sm"
                      onClick={handleGeneratePreview}
                      isLoading={generatingPreview}
                      disabled={generatingBody}
                    >
                      {t('sceneEditor.draftWithAi', '✨ Draft quickly with AI')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleGenerateBody}
                      isLoading={generatingBody}
                      disabled={generatingPreview || !draftBlockId}
                    >
                      {t('sceneEditor.fullWithAi', 'Full expansion (slower)')}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handleGeneratePreview}
                      isLoading={generatingPreview}
                      disabled={generatingBody || savingBody}
                    >
                      {t('sceneEditor.redraftFast', '↻ Quick redraft')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handleGenerateBody}
                      isLoading={generatingBody}
                      disabled={generatingPreview || !draftBlockId || savingBody}
                    >
                      {t('sceneEditor.redraftFull', '↻ Full re-expand')}
                    </Button>
                  </>
                )}
              </div>

              {bodyEmpty && (scene.planPoints?.length ?? 0) === 0 && (
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  {t(
                    'sceneEditor.scenePlanRequiredHint',
                    'Add a few key moments first so AI knows what to draft.'
                  )}
                </p>
              )}

              <AutoResizeTextarea
                minRows={14}
                maxRows={60}
                value={bodyDraft}
                onChange={(e) => setBodyDraft(e.target.value)}
                placeholder={t(
                  'sceneEditor.bodyPlaceholder',
                  'The actual scene — paragraphs, dialogue, sensory detail.'
                )}
              />
              <div className="flex justify-between items-center text-xs text-gray-500">
                <span>
                  {materialHealth.bodyWords} {t('chaptersFirst.words', 'words')}
                </span>
              </div>
            </Card>
          </div>
        </div>
      </main>

      {/* Sticky bottom action bar — save the body */}
      {bodyChanged && (
        <div
          className="fixed bottom-0 left-0 right-0 z-20 border-t border-gray-200 dark:border-gray-800 bg-white/95 dark:bg-gray-900/95 backdrop-blur"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex gap-2 justify-between items-center flex-wrap">
            <span className="text-xs text-amber-700 dark:text-amber-300">
              {t('sceneEditor.unsavedBody', 'Unsaved changes in scene body')}
            </span>
            <Button onClick={handleSaveBody} isLoading={savingBody} className="min-h-[44px]">
              {t('sceneEditor.saveBody', 'Save body')}
            </Button>
          </div>
        </div>
      )}

      <ClaimSuggestionPanel
        isOpen={suggestionPanelOpen}
        onClose={() => setSuggestionPanelOpen(false)}
        suggestions={suggestions}
        isLoading={suggestionsLoading}
        onAddSuggestion={handleAddSuggested}
        onRefreshSuggestions={handleRefreshSuggestions}
        onMoreOfTag={handleMoreOfTag}
        isLoadingMore={moreOfTagLoading}
        loadingMoreTag={moreOfTag}
        mode={suggestionMode}
        onChangeMode={handleChangeSuggestionMode}
      />
    </div>
  );
}
